import {
  PERIOD_PRESET_LABELS,
  PERIOD_PRESETS,
  presetToRange,
  type DateRange,
  type PeriodPreset,
} from "@iagenix/shared";
import { CalendarDays } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";

export interface PeriodValue {
  preset: PeriodPreset | null;
  range: DateRange;
}

export const PERIOD_ALL: PeriodValue = { preset: "maximo", range: { from: null, to: null } };

function fmt(iso: string | null): string {
  if (!iso) return "…";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export function periodLabel(value: PeriodValue): string {
  if (value.preset) return PERIOD_PRESET_LABELS[value.preset];
  return `${fmt(value.range.from)} – ${fmt(value.range.to)}`;
}

/**
 * Seletor de período com presets e intervalo personalizado. Nesta fase filtra por data de
 * criação/atualização; na Fase 6 o mesmo componente passa a definir o período das métricas.
 */
export function PeriodPicker({
  value,
  onChange,
  className,
}: {
  value: PeriodValue;
  onChange: (value: PeriodValue) => void;
  className?: string;
}) {
  const [from, setFrom] = useState(value.range.from ?? "");
  const [to, setTo] = useState(value.range.to ?? "");
  const [open, setOpen] = useState(false);

  const applyCustom = () => {
    if (!from && !to) {
      onChange(PERIOD_ALL);
    } else {
      const a = from || to;
      const b = to || from;
      const [lo, hi] = a <= b ? [a, b] : [b, a];
      onChange({ preset: null, range: { from: lo, to: hi } });
    }
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={className} aria-label="Período">
          <CalendarDays />
          {periodLabel(value)}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel>Período</DropdownMenuLabel>
        {PERIOD_PRESETS.map((p) => (
          <DropdownMenuItem
            key={p}
            onSelect={() => {
              onChange({ preset: p, range: presetToRange(p) });
            }}
            className={value.preset === p ? "font-medium" : undefined}
          >
            {PERIOD_PRESET_LABELS[p]}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <div className="space-y-2 p-2" onKeyDown={(e) => e.stopPropagation()}>
          <p className="text-xs font-medium text-muted-foreground">Intervalo personalizado</p>
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="Data inicial"
            />
            <span className="text-muted-foreground">–</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              aria-label="Data final"
            />
          </div>
          <Button size="sm" className="w-full" onClick={applyCustom}>
            Aplicar
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
